import { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import type { LoanRepayment, LoanFormData } from '../types';

interface LoanRepaymentFormProps {
  loanId: string;
  loanAmount: number;
  remainingBalance: number;
  defaultAmount?: number;
  initialData?: LoanRepayment;
  onSubmit: (data: LoanFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const LoanRepaymentForm = ({
  loanId,
  loanAmount,
  remainingBalance,
  defaultAmount,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false
}: LoanRepaymentFormProps) => {
  const [formData, setFormData] = useState<LoanFormData>({
    amount: initialData?.amount.toString() || (defaultAmount ? String(defaultAmount) : ''),
    paymentMethod: (initialData?.paymentMethod && String(initialData.paymentMethod).trim()) ? initialData.paymentMethod : 'cash',
    reference: initialData?.reference || '',
    paymentDate: initialData ? new Date(initialData.paymentDate) : new Date()
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        amount: initialData.amount.toString(),
        paymentMethod: (initialData.paymentMethod && String(initialData.paymentMethod).trim()) ? initialData.paymentMethod : 'cash',
        reference: initialData.reference || '',
        paymentDate: new Date(initialData.paymentDate)
      });
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Please enter a valid payment amount';
    } else if (amount > remainingBalance + 0.01) {
      newErrors.amount = `Amount cannot exceed remaining balance of ${remainingBalance.toLocaleString()} VT`;
    }

    const paymentMethod = formData.paymentMethod != null ? String(formData.paymentMethod).trim() : '';
    if (!paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {Object.keys(errors).length > 0 && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">
            Please fix the errors below before submitting
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="number"
          label="Payment Amount"
          placeholder="Enter amount"
          value={formData.amount}
          onChange={(e) => {
            setFormData(prev => ({ ...prev, amount: e.target.value }));
            setErrors(prev => ({ ...prev, amount: '' }));
          }}
          error={errors.amount}
          required
          disabled={isSubmitting}
          min="0"
          step="0.01"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Payment Method
          </label>
          <select
            value={formData.paymentMethod ?? 'cash'}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, paymentMethod: e.target.value }));
              setErrors(prev => ({ ...prev, paymentMethod: '' }));
            }}
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-interactive disabled:bg-muted disabled:cursor-not-allowed"
          >
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="check">Check</option>
            <option value="other">Other</option>
          </select>
          {errors.paymentMethod && (
            <p className="mt-1 text-sm text-destructive">{errors.paymentMethod}</p>
          )}
        </div>
      </div>

      <Input
        type="text"
        label="Reference Number"
        placeholder="Enter reference number (optional)"
        value={formData.reference}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, reference: e.target.value }));
        }}
        disabled={isSubmitting}
      />

      <Input
        type="date"
        label="Payment Date"
        value={formData.paymentDate.toISOString().split('T')[0]}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, paymentDate: new Date(e.target.value) }));
        }}
        disabled={isSubmitting}
        required
      />

      <div className="flex items-center gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          fullWidth
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting}
          loading={isSubmitting}
          iconName={initialData ? "Save" : "Plus"}
          iconPosition="right"
          fullWidth
        >
          {initialData ? 'Update Payment' : 'Record Payment'}
        </Button>
      </div>
    </form>
  );
};

export default LoanRepaymentForm;
